import React from "react";
import { IconButton, useTheme, Typography, Link } from "@mui/material";
import CloseIcon from "@icons/myicons/close.svg?react";
import styled from "@emotion/styled";
import { useTranslation } from "react-i18next";
import { getCurrencySymbol } from "@helpers/currencyCodeToSymbol";
import { ComparisonBaseChart } from "@components";
import {
  ComparisonChartRangeSelector,
  DataBlockLayout,
  RangeValue,
} from "@rentablo/components";
import { FundamentalsAutocomplete } from "./components/FundamentalsAutocomplete";
import { ComparisonBlurChart } from "./ComparisonBlurChart";

interface Props {
  isFetching?: boolean;
  defaultRange?: RangeValue;
  data: any;
  dataKey1: string;
  dataKey2?: string;
  onRangeSelect: (value: RangeValue) => void;
  onMetricSelected: (value?: string) => void;
  onHide: () => void;
  metricCurrency1?: string;
  metricCurrency2?: string;
  quoteCurrency: string;
  onlyPro?: boolean;
  noData?: boolean;
}

const FiltersContainer = styled("div")(({ theme }) => ({
  display: "flex",
  width: "100%",
  padding: theme.spacing(0, 4, 4),
}));

const StyledContentBlock = styled(DataBlockLayout)({
  width: "100%",
});

const NoDataContainer = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  textAlign: "center",
  height: "100%",
  padding: theme.spacing(6),
}));

const withCurrency = (label: string, currency?: string) => {
  if (!currency) {
    return label;
  }
  return `${label} (${getCurrencySymbol(currency)})`;
};

export const ComparisonChartWithFilters = ({
  isFetching = false,
  defaultRange,
  data,
  dataKey1,
  dataKey2,
  onRangeSelect,
  onMetricSelected,
  onHide,
  metricCurrency1,
  metricCurrency2,
  quoteCurrency,
  onlyPro,
  noData = false,
}: Props) => {
  const theme = useTheme();
  const { t } = useTranslation();

  const label1 = dataKey1 && t(`fundamentals.metrics.${dataKey1}.short`);
  const shortLabel1 =
    dataKey1 &&
    withCurrency(t(`fundamentals.metrics.${dataKey1}.short`), metricCurrency1);

  const desc1 = dataKey1 && t(`fundamentals.metrics.${dataKey1}.desc`);
  const label2 = dataKey2 && t(`fundamentals.metrics.${dataKey2}.short`);
  const shortLabel2 =
    dataKey2 &&
    withCurrency(t(`fundamentals.metrics.${dataKey2}.short`), metricCurrency2);

  const desc2 = dataKey2 && t(`fundamentals.metrics.${dataKey2}.desc`);

  const title = label2
    ? `${label1} ${t("fundamentals.comparisonChart.to")} ${label2}`
    : label1;

  const quoteLabel = withCurrency(
    t("fundamentals.comparisonChart.quote"),
    quoteCurrency
  );

  return (
    <StyledContentBlock
      title={title}
      isFetching={isFetching}
      toolTipData={{ desc1, desc2 }}
      customActions={
        <IconButton
          size="small"
          onClick={onHide}
          aria-label={t("fundamentals.comparisonChart.remove")}
          data-testid="comparison-chart-close"
        >
          <CloseIcon width={12} height={12} />
        </IconButton>
      }
    >
      <FiltersContainer>
        <ComparisonChartRangeSelector
          defaultValue={defaultRange}
          onSelect={onRangeSelect}
          style={{
            marginRight: theme.spacing(10),
          }}
        />
        <FundamentalsAutocomplete
          defaultValue={dataKey2}
          excludeKey={dataKey1}
          onChange={onMetricSelected}
        />
      </FiltersContainer>

      <div
        style={{
          width: "100%",
          height: "300px",
        }}
      >
        {onlyPro && noData ? (
          <NoDataContainer>
            <Typography variant="body3">
              {t("fundamentals.comparisonChart.noData")}{" "}
              <Link
                href={`mailto:${t("aboutCompany.support.email")}`}
                color="primary"
                underline="hover"
              >
                {t("aboutCompany.support.email")}
              </Link>
            </Typography>
          </NoDataContainer>
        ) : (onlyPro && !noData) || isFetching ? (
          <ComparisonBaseChart
            quoteLabel={quoteLabel}
            data={data}
            shortLabel1={shortLabel1}
            shortLabel2={shortLabel2}
            dataKey1={dataKey1}
            dataKey2={dataKey2}
          />
        ) : (
          <ComparisonBlurChart
            quoteLabel={quoteLabel}
            data={data}
            shortLabel1={shortLabel1}
            shortLabel2={shortLabel2}
            dataKey1={dataKey1}
            dataKey2={dataKey2}
          />
        )}
      </div>
    </StyledContentBlock>
  );
};
